import { useState } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { BangladeshFlag, SwedenFlag } from "@/components/Flags";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/public/contact", form);
      toast.success("Thanks! We'll get back to you within 24 hours.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to send.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative">
        <div className="bg-stage" />
        <div className="absolute inset-0 pointer-events-none">
          <div className="bg-grid" />
          <div className="orb orb-red" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 pt-20 pb-10 sm:pt-28">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[color:var(--ivory-muted)] mb-3">Contact</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">Let's plan your trip.</h1>
          <p className="mt-5 max-w-xl text-[color:var(--ivory-muted)]">
            Drop us a note and a real human will get back to you within 24 hours.
          </p>
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-[1fr_1.4fr] gap-10">
          <aside className="space-y-6">
            <div className="p-6 rounded-2xl border border-white/10 bg-white/[0.02]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-[color:var(--bd-green)]/20 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-[color:var(--bd-green-bright)]" />
                </div>
                <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--ivory-muted)]">Email</div>
              </div>
              <a href="mailto:info@bdtt.se" className="text-lg hover:text-[color:var(--bd-green-bright)]">info@bdtt.se</a>
            </div>

            <div className="p-6 rounded-2xl border border-white/10 bg-white/[0.02]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-[color:var(--bd-red)]/20 flex items-center justify-center">
                  <Phone className="w-4 h-4 text-[color:var(--bd-red-bright)]" />
                </div>
                <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--ivory-muted)]">Working hours</div>
              </div>
              <div className="text-sm text-[color:var(--ivory-muted)] leading-relaxed">
                Mon–Fri · 09:00–18:00 CET<br />
                We reply to all enquiries within 24 hours.
              </div>
            </div>

            <div className="p-6 rounded-2xl border border-[color:var(--bd-green)]/30 bg-gradient-to-br from-[color:var(--bd-green)]/[0.08] to-[color:var(--bd-red)]/[0.05]" data-testid="company-info-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-white" />
                </div>
                <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--bd-green-bright)]">Registered office</div>
              </div>
              <div className="text-base font-semibold text-white mb-1">Bangladesh Travels &amp; Tours AB</div>
              <div className="text-sm text-[color:var(--ivory-muted)] leading-relaxed">
                Kråksätrabacken 27<br />
                127 30 Skärholmen<br />
                Sweden
              </div>
              <div className="mt-4 pt-4 border-t border-white/10 text-sm space-y-1">
                <div>
                  <span className="text-[color:var(--ivory-muted)] text-xs uppercase tracking-[0.2em]">Org.nr</span>{" "}
                  <span className="text-white font-mono ml-2">559562-4361</span>
                </div>
              </div>
              <div className="mt-5 space-y-3 text-sm">
                <div className="flex items-center gap-3">
                  <SwedenFlag className="w-7 h-5 rounded-sm" />
                  <div>
                    <div className="font-semibold">Stockholm, Sweden</div>
                    <div className="text-[color:var(--ivory-muted)] text-xs">By appointment</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <BangladeshFlag className="w-7 h-5 rounded-sm" />
                  <div>
                    <div className="font-semibold">Dhaka, Bangladesh</div>
                    <div className="text-[color:var(--ivory-muted)] text-xs">Operations hub</div>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          <form
            onSubmit={submit}
            className="p-6 sm:p-8 rounded-2xl border border-white/10 bg-white/[0.02]"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Your name" required value={form.name} onChange={update("name")} testId="contact-name" />
              <Field label="Email" type="email" required value={form.email} onChange={update("email")} testId="contact-email" />
              <Field label="Phone (optional)" value={form.phone} onChange={update("phone")} testId="contact-phone" />
              <Field label="Subject" required value={form.subject} onChange={update("subject")} testId="contact-subject" />
            </div>
            <div className="mt-4">
              <label className="text-xs uppercase tracking-[0.25em] text-[color:var(--ivory-muted)] mb-2 block">
                Message<span className="text-[color:var(--bd-red-bright)]"> *</span>
              </label>
              <textarea
                value={form.message}
                onChange={update("message")}
                required
                rows={6}
                className="email-input pl-4 py-3"
                style={{ borderRadius: "16px", resize: "vertical" }}
                placeholder="Tell us where you'd like to go and when..."
                data-testid="contact-message"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="mt-6 pill pill-primary inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="contact-submit"
            >
              {submitting ? "Sending…" : "Send message"} <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.25em] text-[color:var(--ivory-muted)] mb-2 block">
        {label}
        {required && <span className="text-[color:var(--bd-red-bright)]"> *</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="email-input pl-4"
        data-testid={testId}
      />
    </div>
  );
}
